import {
  $createParagraphNode,
  $getRoot,
  $isElementNode,
  $isLineBreakNode,
  $isTextNode,
  type LexicalEditor,
  type LexicalNode,
} from "lexical";

import { $generateNodesFromDOM } from "@lexical/html";
import { $isLinkNode } from "@lexical/link";
import { DYNAMIC_FOOTNOTE_ATTRIBUTE } from "@vega-core/components/HtmlContent/DynamicFootnote";
import { DYNAMIC_TOOLTIP_ATTRIBUTE } from "@vega-core/components/HtmlContent/DynamicTooltip";

import { $isFootnoteNode } from "./footnoteNode";
import { $isTooltipNode } from "./tooltipNode";

const escapeText = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const escapeAttr = (text: string) => escapeText(text).replace(/"/g, "&quot;");

// Footnote bodies are emitted as-is into the marker span. Round-tripping
// through innerHTML lets the parser discard unbalanced/stray markup (e.g. a
// lone "</span>") so a footnote body can't break out of its own span and
// corrupt the surrounding field. This normalises structure only — it is not a
// sanitizer; stripping unsafe tags/attributes is the HtmlContent follow-up.
const balanceHtml = (html: string): string => {
  const container = document.createElement("div");
  container.innerHTML = html;

  return container.innerHTML;
};

const looksLikeHtml = (value: string): boolean =>
  /<\/?[a-z][\s\S]*>/i.test(value);

/**
 * Server presets and legacy drafts may arrive as plain text with newlines.
 * Convert those to <br> so the editor preserves line breaks; pass real HTML
 * through unchanged.
 */
export const toEditorHtml = (raw: string): string =>
  looksLikeHtml(raw) ? raw : escapeText(raw).replace(/\r?\n/g, "<br>");

export const editorStateToHtml = (editor: LexicalEditor): string =>
  editor.getEditorState().read(() => {
    const root = $getRoot();
    const acc = { footnotes: 0, hasMarker: false };

    const serialize = (node: LexicalNode): string => {
      if ($isLineBreakNode(node)) return "<br>";

      if ($isFootnoteNode(node)) {
        acc.footnotes += 1;
        acc.hasMarker = true;

        return `<span ${DYNAMIC_FOOTNOTE_ATTRIBUTE}="${acc.footnotes}">${balanceHtml(node.getContent())}</span>`;
      }

      if ($isTooltipNode(node)) {
        acc.hasMarker = true;

        return `<span ${DYNAMIC_TOOLTIP_ATTRIBUTE}="${escapeAttr(node.getContent())}"></span>`;
      }

      if ($isTextNode(node)) {
        let html = escapeText(node.getTextContent());

        if (!html) return "";

        if (node.hasFormat("bold")) html = `<b>${html}</b>`;

        if (node.hasFormat("italic")) html = `<i>${html}</i>`;

        if (node.hasFormat("underline")) html = `<u>${html}</u>`;

        return html;
      }

      if ($isLinkNode(node)) {
        const inner = node.getChildren().map(serialize).join("");

        return `<a href="${escapeAttr(node.getURL())}">${inner}</a>`;
      }

      if ($isElementNode(node)) {
        return node.getChildren().map(serialize).join("");
      }

      return "";
    };

    const html = root
      .getChildren()
      .map((child) => serialize(child))
      .join("<br>");

    if (!acc.hasMarker && root.getTextContent().trim() === "") return "";

    return html;
  });

export const $populateFromHtml = (
  editor: LexicalEditor,
  html: string,
): void => {
  const root = $getRoot();

  root.clear();

  if (html) {
    const dom = new DOMParser().parseFromString(html, "text/html");
    const nodes = $generateNodesFromDOM(editor, dom);
    let paragraph: ReturnType<typeof $createParagraphNode> | null = null;

    for (const node of nodes) {
      if ($isElementNode(node) && !node.isInline()) {
        if (paragraph) {
          root.append(paragraph);
          paragraph = null;
        }

        root.append(node);
      } else {
        paragraph ??= $createParagraphNode();
        paragraph.append(node);
      }
    }

    if (paragraph) root.append(paragraph);
  }

  if (root.getChildrenSize() === 0) {
    root.append($createParagraphNode());
  }
};
